import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../Utils/baseQueryWithReauth";
import { ICVCCustomersResponse, ICVCCustomerAIChatRequest, ICVCCustomerAIChatResponse, ICVCCustomerInlineNote } from "../components/CVCCustomer/CVCCustomer.interfaces";
import { ENDPOINTS } from "../Services/ServiceConfig";

// Feature flag: Enable static file fetching (set to true when static files are deployed)
// When false, goes directly to API (Redis → Kusto) without 404 latency from missing files
const ENABLE_STATIC_FILE_FETCH = false;

// Get API root URL (remove /api/ suffix for static files)
const getStaticFileBaseUrl = () => {
  const apiUrl = ENDPOINTS.BASE_URL || "";
  return apiUrl.replace(/\/api\/?$/, "");
};

/**
 * API slice for CVC Customer functionality
 *
 * Data Flow:
 * - ENABLE_STATIC_FILE_FETCH=true: Static JSON file first (~50ms), then API fallback
 * - ENABLE_STATIC_FILE_FETCH=false: API directly (Redis → Kusto, ~50ms with prewarm)
 *
 * Admin generates static files via POST /api/admin/staticData/cvcCustomer/generate
 */
export const cvcCustomerAPI = createApi({
  reducerPath: "cvcCustomer",
  tagTypes: ["CVCCustomers"],
  baseQuery: baseQueryWithReauth,
  // Keep data for 24 hours - static data
  keepUnusedDataFor: 86400,
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  endpoints: (builder) => ({
    /**
     * Fetches all CVC customers.
     * Tries static JSON file first (~50ms), falls back to API → Redis → Kusto.
     */
    getCVCCustomers: builder.query<ICVCCustomersResponse, void>({
      queryFn: async (_arg, _queryApi, _extraOptions, baseQuery) => {
        // Try static file first if enabled (avoids 404 latency when files don't exist)
        if (ENABLE_STATIC_FILE_FETCH) {
          try {
            const staticUrl = `${getStaticFileBaseUrl()}/data/cvcCustomer/customers.json`;
            const staticResponse = await fetch(staticUrl);
            if (staticResponse.ok) {
              const staticData = await staticResponse.json() as ICVCCustomersResponse;
              return { data: staticData };
            }
          } catch {
            // Static file doesn't exist, fall through to API
          }
        }

        // API (Redis → Kusto) - prewarm service keeps Redis hot
        const result = await baseQuery({
          url: "cvc-customer/customers",
          method: "GET"
        });
        
        if (result.error) {
          return { error: result.error };
        }
        
        return { data: result.data as ICVCCustomersResponse };
      },
      providesTags: ["CVCCustomers"],
      keepUnusedDataFor: 86400
    }),

    /**
     * Fetches the full notes for a single customer + product engagement on demand.
     * Keeps the heavy note text out of the main list payload for faster page loads.
     */
    getCVCCustomerNotes: builder.query<ICVCCustomerInlineNote[], { customerName: string; productName: string }>({
      query: ({ customerName, productName }) => ({
        url: "cvc-customer/notes",
        method: "GET",
        params: { customerName, productName }
      })
    }),

    /**
     * AI Assistant Chat - Ask questions about CVC Customer data
     * Sends the user's question along with summarized data context to OpenAI
     * Returns AI-generated response based on the data
     */
    askCVCCustomerAIAssistant: builder.mutation<ICVCCustomerAIChatResponse, ICVCCustomerAIChatRequest>({
      query: (request) => ({
        url: "cvc-customer/ai-assistant/chat",
        method: "POST",
        body: request
      })
    })
  })
});

export const {
  useGetCVCCustomersQuery,
  useLazyGetCVCCustomerNotesQuery,
  useAskCVCCustomerAIAssistantMutation
} = cvcCustomerAPI;
